import withKycRequired from "@rentiohq/shared-frontend/dist/hoc/withKycRequired";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Form,
  Box,
  Button,
  MainCard,
  Typography,
  Divider,
  Stack,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import queryString from "query-string";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { addBankAccountSchema } from "../../../../forms/addBankAccount";
import { AddBankAccountStyles } from "./AddBankAccount.styles";

const AddBankAccountBase = () => {
  const sx = AddBankAccountStyles();

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state: IPartialRootState) =>
    authSelectors.getUser(state),
  );

  const isAddingIban = useSelector((state: IPartialRootState) =>
    contactSelectors.isCreatingIban(state),
  );

  const createIbanError = useSelector((state: IPartialRootState) =>
    contactSelectors.createIbanError(state),
  );

  React.useEffect(() => {
    if (createIbanError) {
      enqueueSnackbar({
        message: getLocalizedText(
          // @ts-ignore
          createIbanError?.response?.data?.error?.message || "system.error",
        ),
        variant: "error",
      });
    }
  }, [createIbanError]);

  const handleCustomSuccessAction = (ibanId?: number) => {
    if (!ibanId) {
      return;
    }

    const state = location.state as undefined | { referrer?: string };

    const referrerPath = queryString.parseUrl(state?.referrer || "/");
    referrerPath.query.ibanId = `${ibanId}`;

    // @ts-ignore
    const resultPath = queryString.stringifyUrl(referrerPath);
    // navigate(`/payment/${paymentRequestId}/payment-method`);
    navigate(resultPath);
  };

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const handleSubmit = (values: any) => {
    dispatch(
      contactActions.addIban.actions.start({
        contactId: user?.personalContactId,
        iban: values,
        onSuccess: id => handleCustomSuccessAction(id),
      }),
    );
  };

  const renderActions = () => (
    <Stack direction="row" gap={1} mt={2.5}>
      <Button
        variant={EButtonVariants.Outlined}
        sx={sx.content.actionBtn}
        onClick={() => navigate(`/payment/${paymentRequestId}/payment-method`)}
      >
        {getLocalizedText("system.cancel")}
      </Button>
      <Button
        variant={EButtonVariants.Contained}
        type="submit"
        sx={sx.content.actionBtn}
      >
        {getLocalizedText("system.save")}
      </Button>
    </Stack>
  );

  return (
    <MainCard content={false}>
      <Box py={2} px={3}>
        <Typography variant="h4" sx={sx.content.heading}>
          {getLocalizedText("tenant.payments.add_bank.title")}
        </Typography>
        <Typography
          variant="caption"
          dangerouslySetInnerHTML={{
            __html: getLocalizedText("tenant.payments.add_bank.info"),
          }}
        />
      </Box>
      <Divider />

      <Box py={2} px={3}>
        {isAddingIban ? (
          <SpinningLoader />
        ) : (
          <Form
            onSubmit={handleSubmit}
            getSchema={addBankAccountSchema({
              ownerName: user ? getName(user) : undefined,
              validateSepaSupported: true,
            })}
            customActions={renderActions()}
          />
        )}
      </Box>
    </MainCard>
  );
};

export const AddBankAccount = withKycRequired(AddBankAccountBase);
